/**
 * Tenant Types for Frontend
 * Mirrors backend types for type safety
 */

// =============================================================================
// Plan Types
// =============================================================================

export type PlanType = 'VEREADOR' | 'DEPUTADO' | 'SENADOR';
export type SubscriptionStatus = 'active' | 'trial' | 'suspended' | 'cancelled';

// =============================================================================
// Theme Types
// =============================================================================

export type ColorPaletteId =
  | 'institutional-blue'
  | 'popular-red'
  | 'progressive-purple'
  | 'sustainable-green'
  | 'center-teal'
  | 'conservative-navy'
  | 'liberal-yellow'
  | 'youth-pink'
  | 'custom';

export type TypographyPresetId =
  | 'institutional'
  | 'modern'
  | 'popular'
  | 'editorial'
  | 'bold'
  | 'classic'
  | 'custom';

export interface TenantTheme {
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string;
  faviconUrl: string;
  /** @deprecated use fontBody / fontDisplay instead. */
  fontFamily?: string;
  accentColor?: string;
  fontDisplay?: string;
  fontBody?: string;
  colorPalette?: ColorPaletteId;
  typographyPreset?: TypographyPresetId;
}

// =============================================================================
// Public Page Types (Santinho Digital)
// =============================================================================

// Brazilian political positions for mandates dropdown
export const BRAZILIAN_POLITICAL_POSITIONS = [
  'Vereador',
  'Vice-Prefeito',
  'Prefeito',
  'Deputado Estadual',
  'Deputado Distrital',
  'Deputado Federal',
  'Vice-Governador',
  'Governador',
  'Senador',
  'Vice-Presidente',
  'Presidente',
] as const;

export type BrazilianPoliticalPosition = (typeof BRAZILIAN_POLITICAL_POSITIONS)[number];

// Political mandate with location
export interface PoliticalMandate {
  position: BrazilianPoliticalPosition;
  year: number;
  location: string; // City/State - e.g., "São Paulo/SP"
}

// Gallery image with optional caption
export interface GalleryImage {
  url: string;
  caption?: string;
}

// Approved project (lei, projeto de lei, etc)
export interface ApprovedProject {
  name: string; // Name/title of the project
  mandate: BrazilianPoliticalPosition; // Position when project was approved
  year: number; // Year of approval
  description?: string; // Optional description
}

// Template options for santinho design
export type SantinhoTemplate = 'classic' | 'modern' | 'bold' | 'minimal' | 'campaign';

export const SANTINHO_TEMPLATES: { id: SantinhoTemplate; name: string; description: string }[] = [
  {
    id: 'classic',
    name: 'Clássico',
    description: 'Layout tradicional, foto à esquerda, texto à direita',
  },
  {
    id: 'modern',
    name: 'Moderno',
    description: 'Cover full-width, foto candidato circular sobreposta',
  },
  {
    id: 'bold',
    name: 'Impactante',
    description: 'Cores vibrantes, tipografia grande, foto em destaque',
  },
  {
    id: 'minimal',
    name: 'Minimalista',
    description: 'Clean, muito espaço em branco, foco no conteúdo',
  },
  {
    id: 'campaign',
    name: 'Campanha',
    description: 'Estilo campanha política, bandeiras, número grande',
  },
];

export interface PublicPageContent {
  candidatePhoto?: string; // Photo of the candidate
  coverPhoto?: string; // Cover/hero image (renamed from heroImage)
  heroImage?: string; // @deprecated - use coverPhoto instead (kept for backward compatibility)
  candidateNumber?: string; // Voting number (e.g., "45123")
  biography?: string;
  proposals?: string[];
  achievements?: string[];
  mandates?: PoliticalMandate[]; // Previous political mandates
  approvedProjects?: ApprovedProject[]; // Approved projects/laws
  gallery?: GalleryImage[]; // Image gallery (max 10)
}

export interface SocialLinks {
  instagram?: string;
  facebook?: string;
  twitter?: string;
  tiktok?: string;
  youtube?: string;
  website?: string;
}

export interface WebchatConfig {
  enabled: boolean;
  headerTitle?: string;
  headerSubtitle?: string;
  welcomeMessage?: string;
}

// =============================================================================
// Block-based layout (Editor WYSIWYG)
// =============================================================================

export type BlockType =
  | 'hero'
  | 'bio'
  | 'proposals'
  | 'achievements'
  | 'mandates'
  | 'approved-projects'
  | 'gallery'
  | 'video'
  | 'cta'
  | 'social'
  | 'custom-text'
  | 'number-card'
  | 'countdown'
  | 'testimonials'
  | 'spacer'
  | 'divider';

export type BlockAlignment = 'left' | 'center' | 'right';
export type BlockBackground = 'surface' | 'primary' | 'secondary' | 'accent' | 'transparent';

export interface BlockStyle {
  background?: BlockBackground;
  alignment?: BlockAlignment;
  paddingTop?: 'sm' | 'md' | 'lg' | 'xl';
  paddingBottom?: 'sm' | 'md' | 'lg' | 'xl';
}

export type BlockProps = Record<string, unknown>;

export interface Block {
  id: string;
  type: BlockType;
  visible: boolean;
  order: number;
  props: BlockProps;
  style?: BlockStyle;
}

export interface PublicPageSeo {
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
}

export interface PublicPageConfig {
  enabled: boolean;
  title: string;
  description: string;
  template: SantinhoTemplate;
  content: PublicPageContent;
  socialLinks: SocialLinks;
  webchat?: WebchatConfig;
  blocks?: Block[];
  presetId?: string;
  seo?: PublicPageSeo;
}

// =============================================================================
// Tenant Config (returned by public API)
// =============================================================================

export interface TenantPublicConfig {
  slug: string;
  name: string;
  theme: TenantTheme;
  publicPage: PublicPageConfig;
}

// =============================================================================
// Full Tenant (returned by settings API)
// =============================================================================

export interface Tenant {
  id: string;
  slug: string;
  name: string;
  plan: PlanType;
  status: 'active' | 'suspended' | 'cancelled';
  theme: TenantTheme;
  publicPage: PublicPageConfig;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// Subscription
// =============================================================================

export interface PlanLimits {
  contacts: number;
  messages_per_month: number;
  users: number;
  whatsapp_numbers: number;
  custom_roles: number;
}

export interface SubscriptionUsage {
  contacts_count: number;
  messages_this_month: number;
  messages_reset_at: string;
  users_count: number;
  whatsapp_numbers_count: number;
  custom_roles_count: number;
}

export interface Subscription {
  id: string;
  plan: PlanType;
  billingPeriod: 3 | 6 | 12;
  limits: PlanLimits;
  usage: SubscriptionUsage;
  pricing: {
    monthly_price: number;
    total_price: number;
    discount_percent: number;
  };
  status: SubscriptionStatus;
  startDate: string;
  endDate: string;
  autoRenew: boolean;
}

// =============================================================================
// Default Theme
// =============================================================================

export const DEFAULT_THEME: TenantTheme = {
  primaryColor: '#2563eb',
  secondaryColor: '#1e40af',
  logoUrl: '',
  faviconUrl: '',
};

// =============================================================================
// Reserved Slugs
// =============================================================================

export const RESERVED_SLUGS = [
  'www',
  'app',
  'api',
  'dev',
  'staging',
  'sb',
  'admin',
  'mail',
  'smtp',
  'ftp',
  'cdn',
  'static',
  'assets',
  'blog',
  'help',
  'support',
  'status',
  'docs',
] as const;
